import { get } from "lodash";
import { SagaIterator } from "redux-saga";
import { all, call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { Filter } from "constants/filters";
import { NEW_TICKET_TEMP_ID, SESSION_STORAGE_DRAFT_KEY } from "constants/tickets/drafts";
import { getIsMemberOfTicketTeam } from "helpers/tickets";
import { ITicket, TicketSilo, TicketStatus } from "interfaces/entities/ticket";
import { ApiManager } from "services/api";
import { TicketSerializer } from "services/api/ticket";
import { ISendTicketMessageParams } from "services/api/ticket/ticket-api";
import { getItem } from "services/session-storage";
import { getCurrentAgentId } from "store/features/session";
import { IActionWithPayload } from "store/helper";
import { TicketsViewActions, TicketsViewActionsNames } from "store/views/tickets/actions";
import {
  getCurrentPage,
  getTicket,
  selectCanManageTickets,
  selectSuggestedTagIds,
} from "store/views/tickets/selectors";
import { removeDuplicates } from "utils/array";

import { CRUDAction, RequestAction } from "../actions";
import { selectLoggedInAgent } from "../agents";
import { selectTags } from "../tags";

import { TICKET, TicketActions } from "./actions";
import {
  IAddTicketTagsPayload,
  IApplyMacroPayload,
  ICreateTicketPayload,
  IDeleteTicketPayload,
  IFetchClosestTicketPayload,
  IFetchRecentTicketsPayload,
  IFetchSimilarTicketsPayload,
  IFetchTagSuggestionsPayload,
  IFetchTicketPayload,
  IFetchTicketsCollectionPayload,
  IFetchTicketsToMergePayload,
  IRemoveTicketTagsPayload,
  ISendTicketMessagePayload,
  ISummarizeTicketPayload,
  IUnspamPayload,
  IUpdateAssignedTeamPayload,
  IUpdatePriorityPayload,
  IUpdateStatusPayload,
  IUpdateTicketAssigneePayload,
  IUpdateTicketCCsPayload,
  IUpdateTicketCustomFieldPayload,
  IUpdateTicketRequesterPayload,
  IUpdateTicketSiloPayload,
  IUpdateTicketSubjectPayload,
  IUpdateTicketTagsPayload,
} from "./interfaces";

export function* fetch(
  action: IActionWithPayload<string, IFetchTicketPayload & { overwriteCurrent: boolean; incrementCounter: boolean }>
): SagaIterator {
  const { id, overwriteCurrent = false, incrementCounter = false } = action.payload;
  const agent = yield select(selectLoggedInAgent);
  const suggestedTagIds = yield select(selectSuggestedTagIds);
  const hasRightToManageTicket = yield select(selectCanManageTickets);

  const response = yield call(ApiManager.ticketApi.fetch, id);

  if (response.result) {
    const ticket = TicketSerializer.deserialize(response.result);

    yield put(
      TicketActions.fetchSuccess({
        id: ticket.id,
        value: ticket,
        agent,
        overwriteCurrent,
        incrementCounter,
      })
    );

    const shouldFetchTagSuggestions =
      hasRightToManageTicket &&
      ticket.silo === TicketSilo.Tickets &&
      ticket.parentTicket === null &&
      getIsMemberOfTicketTeam(agent, ticket) &&
      !suggestedTagIds;

    if (shouldFetchTagSuggestions) {
      yield put(TicketActions.fetchTagSuggestions({ ticketId: ticket.id }));
    }
  } else {
    yield put(TicketActions.fetchFailure({ id, error: "Not found" }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* summarizeTicket(action: IActionWithPayload<string, ISummarizeTicketPayload>) {
  const { ticketId } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.summarize, ticketId);

  if (response.result) {
    yield put(TicketActions.summarizeTicketSuccess({ ticketId }));
  } else {
    yield put(TicketActions.summarizeTicketFailure({ error: response.error.error, ticketId }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* applyMacro(action: IActionWithPayload<string, IApplyMacroPayload>) {
  const { ticketId, actions, macroId } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.applyMacro, ticketId, actions, macroId);

  if (response.result) {
    yield put(TicketActions.applyMacroSuccess({}));
  } else {
    yield put(TicketActions.applyMacroFailure({ error: response.error.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* fetchClosestTicket(action: IActionWithPayload<string, IFetchClosestTicketPayload>) {
  const { ticketInfo } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.fetchClosestTicket, action.payload);

  if (response.result) {
    if (response.result.length === 0) {
      yield put(
        TicketActions.fetchClosestTicketSuccess({
          ticket: null,
          ticketInfo,
        })
      );
      return;
    }
    const ticket = TicketSerializer.deserialize(response.result[0]);
    yield put(TicketActions.fetchClosestTicketSuccess({ ticket, ticketInfo }));
  } else {
    yield put(TicketActions.fetchClosestTicketFailure({ error: response.error.message }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* fetchCollection(action: IActionWithPayload<TicketsViewActionsNames, IFetchTicketsCollectionPayload>) {
  const params = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const ticketsResponse = yield call(ApiManager.ticketApi.fetchAll, params);

  if (ticketsResponse.result) {
    const pages = parseInt(ticketsResponse.responseHeaders.get("X-Total-Pages"), 10);
    const total = parseInt(ticketsResponse.responseHeaders.get("X-Total-Results"), 10);
    const tickets = ticketsResponse.result;

    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const currentPage = yield select(getCurrentPage);
    if (pages > 0 && currentPage > pages) {
      yield put(TicketsViewActions.setPage(pages));
      return;
    }

    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    let ticketDrafts = yield call(getItem, SESSION_STORAGE_DRAFT_KEY);
    ticketDrafts = ticketDrafts || {};
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ticketsData: ITicket[] = TicketSerializer.deserializeCollection(tickets).map((ticket: any) => {
      const ticketWithDraft = ticket;
      const draft = ticketDrafts[ticket.id];
      if (draft) {
        ticketWithDraft.draft = draft;
      }
      return ticketWithDraft;
    });

    yield put(
      TicketsViewActions.setTicketsData({
        data: ticketsData,
        totalPages: pages,
        totalResults: total,
      })
    );

    yield put(TicketActions.fetchCollectionSuccess({ values: ticketsData }));
  } else {
    yield put(TicketActions.fetchCollectionFailure({ error: ticketsResponse.error.message }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* unspam(action: IActionWithPayload<string, IUnspamPayload>) {
  const { ticketId } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.unspam, ticketId);
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.unspamSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.unspamFailure());
  }
}

export function* updateTags(action: IActionWithPayload<string, IUpdateTicketTagsPayload>): SagaIterator {
  const { ticketId, tagIds } = action.payload;
  const response = yield call(ApiManager.ticketApi.updateTags, ticketId, { tagIds });
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updateTagsSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateTagsFailure({ error: response?.error?.error }));
  }
}

export function* addTags(action: IActionWithPayload<string, IAddTicketTagsPayload>): SagaIterator {
  const { ticketId, tagIds, suggestedTagIds } = action.payload;
  const agent = yield select(selectLoggedInAgent);

  const response = yield call(ApiManager.ticketApi.addTags, ticketId, {
    tagIds,
    suggestedTagIds,
  });

  if (response.result) {
    yield put(TicketActions.addTagsSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.addTagsFailure({ error: response.error?.error }));
  }
}

export function* removeTag(action: IActionWithPayload<string, IRemoveTicketTagsPayload>): SagaIterator {
  const { ticketId, tagId } = action.payload;

  const response = yield call(ApiManager.ticketApi.removeTag, ticketId, tagId);

  if (response.result) {
    yield put(TicketActions.removeTagSuccess(TicketSerializer.deserialize(response.result)));
  } else {
    yield put(TicketActions.removeTagFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updatePriority(action: IActionWithPayload<string, IUpdatePriorityPayload>) {
  const { ticketId, priority } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updatePriority, ticketId, priority);
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updatePrioritySuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updatePriorityFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateStatus(action: IActionWithPayload<string, IUpdateStatusPayload>) {
  const { ticketId, status } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateStatus, ticketId, status);
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updateStatusSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateStatusFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateSilo(action: IActionWithPayload<string, IUpdateTicketSiloPayload>) {
  const { ticketId, silo } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateSilo, ticketId, { silo });

  if (response.result) {
    yield put(TicketActions.updateSiloSuccess(silo));
  } else {
    yield put(TicketActions.updateSiloFailure({ silo, error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateSubject(action: IActionWithPayload<string, IUpdateTicketSubjectPayload>) {
  const { ticketId, subject } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateSubject, ticketId, { subject });
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updateSubjectSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateSubjectFailure({ error: response.error.message }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateRequester(action: IActionWithPayload<string, IUpdateTicketRequesterPayload>) {
  const { ticketId, requesterName, requesterEmail } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateRequester, ticketId, { requesterName, requesterEmail });
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updateRequesterSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateRequesterFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateCCs(action: IActionWithPayload<string, IUpdateTicketCCsPayload>) {
  const { ticketId, ccs } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateCCs, ticketId, { ccs });
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updateCCsSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateCCsFailure({ error: response.error?.error }));
  }
}
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateCustomField(action: IActionWithPayload<string, IUpdateTicketCustomFieldPayload>) {
  const { ticketId, customFields } = action.payload;
  yield delay(500);
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateCustomField, ticketId, { customFields });
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updateCustomFieldSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateCustomFieldFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateAssignee(action: IActionWithPayload<string, IUpdateTicketAssigneePayload>) {
  const { ticketId, assigneeId } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateAssignee, ticketId, { assigneeId });
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);
  if (response.result) {
    yield put(TicketActions.updateAssigneeSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateAssigneeFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* updateAssignedTeam(action: IActionWithPayload<string, IUpdateAssignedTeamPayload>) {
  const { ticketId, teamId } = action.payload;
  const ticket: ITicket = yield select(getTicket);
  const visibilityTeamIds = removeDuplicates(ticket.teamIds.concat(teamId));

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.updateAssignment, ticketId, { teamId, visibilityTeamIds });
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  if (response.result) {
    yield put(TicketActions.updateAssignedTeamSuccess(TicketSerializer.deserialize(response.result), agent));
  } else {
    yield put(TicketActions.updateAssignedTeamFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* sendMessage(action: IActionWithPayload<string, ISendTicketMessagePayload>) {
  const {
    ticketId,
    message,
    isPrivate,
    status,
    attachments,
    transactionID,
    richTextPresent,
    cannedResponseIds,
    replySuggestionIds,
  } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const agent = yield select(selectLoggedInAgent);

  const apiPayload: ISendTicketMessageParams = {
    message,
    isPrivate,
    attachments,
    transactionID,
    richTextPresent,
    cannedResponseIds,
    replySuggestionIds,
  };
  if (status) {
    apiPayload.status = status;
  }
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const ticket = yield select(getTicket);
  if (!ticket.assignee && message && !isPrivate) {
    apiPayload.assigneeId = yield select(getCurrentAgentId);
  }

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.sendMessage, ticketId, apiPayload);

  if (response.result) {
    yield put(
      TicketActions.sendMessageSuccess(TicketSerializer.deserialize(response.result), { ...action.payload }, agent)
    );
    yield put(TicketsViewActions.clearDraft({ ticketId }));
  } else {
    const errors = get(response, "error.error.details.errors");
    const error = get(response, "error.error.message");
    const type = get(response, "error.error.type");

    if (type === "limitExceeded") {
      yield put(TicketActions.sendMessageFailure({ reason: type, error }));
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } else if (errors && errors.some((e: any) => e.field && /^attachments(.*)size$/.test(e.field))) {
      yield put(TicketActions.sendMessageFailure({ reason: "File size exceeds 10 MB." }));
    } else {
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
      yield put(TicketActions.sendMessageFailure({ reason: null }));
    }
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createTicket(action: IActionWithPayload<string, ICreateTicketPayload>) {
  const { payload } = action;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.createTicket, payload);
  if (response.result) {
    yield put(TicketActions.createTicketSuccess(TicketSerializer.deserialize(response.result)));
    yield put(TicketsViewActions.clearDraft({ ticketId: NEW_TICKET_TEMP_ID }));
  } else {
    yield put(
      TicketActions.createTicketFailure({
        error: response.error.error.message,
        type: response.error.error.type,
        details: response.error.error.details,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteTicket(action: IActionWithPayload<string, IDeleteTicketPayload>) {
  const { id: ticketId, shouldRedirect } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.deleteTicket, ticketId);
  if (response.result) {
    yield put(TicketActions.deleteTicketSuccess({ id: ticketId, shouldRedirect }));
  } else {
    yield put(TicketActions.deleteTicketFailure({ error: "Cannot delete ticket" }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* fetchRecentTickets(action: IActionWithPayload<string, IFetchRecentTicketsPayload>) {
  const { ticketId, requesterEmail } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.fetchAll, {
    filters: {
      [Filter.RequesterEmail]: [requesterEmail],
    },
    eventsScope: "none",
    pageSize: 7,
    maxCount: 0,
  });

  if (response.result) {
    const ticketsData: ITicket[] = TicketSerializer.deserializeCollection(response.result).filter(
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ticket: any) => ticket.id !== ticketId
    );

    yield put(TicketActions.fetchRecentTicketsSuccess({ values: ticketsData, ticketId }));
  } else {
    yield put(TicketActions.fetchRecentTicketsFailure({ error: response.error.message }));
  }
}

export function* fetchTagSuggestions(action: IActionWithPayload<string, IFetchTagSuggestionsPayload>): SagaIterator {
  const { ticketId } = action.payload;

  const response = yield call(ApiManager.ticketApi.fetchTagSuggestions, ticketId);

  if (response.result) {
    // tagIDs may be cached in Redis, so we must ensure they exclude any tags that were removed from the license during the cache duration
    const existingTagIds = yield select(selectTags);
    const filteredTagIds = response.result.filter((tagId: string) => existingTagIds[tagId]);
    yield put(TicketActions.fetchTagSuggestionsSuccess({ ticketId, tagIds: filteredTagIds }));
  } else {
    yield put(TicketActions.fetchTagSuggestionsFailure({ error: response.error?.message }));
  }
}

export function* fetchSimilarTickets(action: IActionWithPayload<string, IFetchSimilarTicketsPayload>): SagaIterator {
  const { ticketId } = action.payload;
  const response = yield call(ApiManager.ticketApi.fetchSimilarTickets, { ticketId });

  if (response.result) {
    const tickets = TicketSerializer.deserializeSimilarTickets(response.result);
    yield put(TicketActions.fetchSimilarTicketsSuccess({ values: tickets, ticketId }));
  } else {
    yield put(TicketActions.fetchSimilarTicketsFailure({ error: response?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* fetchTicketsToMerge(action: IActionWithPayload<string, IFetchTicketsToMergePayload>) {
  const { ticketId, requesterEmail } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.ticketApi.fetchTicketsToMerge, {
    filters: {
      [Filter.TicketStatus]: [TicketStatus.Open, TicketStatus.Pending, TicketStatus.OnHold],
      [Filter.RequesterEmail]: [requesterEmail],
    },
  });

  if (response.result) {
    const ticketsData: ITicket[] = TicketSerializer.deserializeCollection(response.result).filter(
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ticket: any) => ticket.id !== ticketId
    );

    yield put(TicketActions.fetchtTicketsToMergeSuccess({ values: ticketsData, ticketId }));
  } else {
    yield put(TicketActions.fetchtTicketsToMergeFailure({ error: response.error.message }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* ticketSagas() {
  yield all([
    takeEvery(TICKET.CREATE[RequestAction.REQUEST], createTicket),
    takeEvery(TICKET.DELETE[RequestAction.REQUEST], deleteTicket),
    takeEvery(TICKET[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST], fetch),
    takeLatest(TICKET[CRUDAction.FETCH_COLLECTION][RequestAction.REQUEST], fetchCollection),
    takeEvery(TICKET.UPDATE_PRIORITY[RequestAction.REQUEST], updatePriority),
    takeEvery(TICKET.UPDATE_TAGS[RequestAction.REQUEST], updateTags),
    takeEvery(TICKET.ADD_TAGS[RequestAction.REQUEST], addTags),
    takeEvery(TICKET.REMOVE_TAG[RequestAction.REQUEST], removeTag),
    takeEvery(TICKET.UPDATE_SUBJECT[RequestAction.REQUEST], updateSubject),
    takeEvery(TICKET.UPDATE_STATUS[RequestAction.REQUEST], updateStatus),
    takeEvery(TICKET.UPDATE_REQUESTER[RequestAction.REQUEST], updateRequester),
    takeEvery(TICKET.UPDATE_CCS[RequestAction.REQUEST], updateCCs),
    takeLatest(TICKET.UPDATE_CUSTOM_FIELD[RequestAction.REQUEST], updateCustomField),
    takeEvery(TICKET.UPDATE_SILO[RequestAction.REQUEST], updateSilo),
    takeEvery(TICKET.UPDATE_ASSIGNEE[RequestAction.REQUEST], updateAssignee),
    takeEvery(TICKET.UPDATE_ASSIGNED_TEAM[RequestAction.REQUEST], updateAssignedTeam),
    takeEvery(TICKET.SEND_MESSAGE[RequestAction.REQUEST], sendMessage),
    takeEvery(TICKET.UNSPAM[RequestAction.REQUEST], unspam),
    takeEvery(TICKET.FETCH_RECENT_TICKETS[RequestAction.REQUEST], fetchRecentTickets),
    takeEvery(TICKET.FETCH_TAG_SUGGESTIONS[RequestAction.REQUEST], fetchTagSuggestions),
    takeEvery(TICKET.FETCH_SIMILAR_TICKETS[RequestAction.REQUEST], fetchSimilarTickets),
    takeEvery(TICKET.FETCH_TICKETS_TO_MERGE[RequestAction.REQUEST], fetchTicketsToMerge),
    takeEvery(TICKET.FETCH_CLOSEST_TICKET[RequestAction.REQUEST], fetchClosestTicket),
    takeEvery(TICKET.SUMMARIZE_TICKET[RequestAction.REQUEST], summarizeTicket),
    takeEvery(TICKET.APPLY_MACRO[RequestAction.REQUEST], applyMacro),
  ]);
}
